import type {
  AgentDetailCardElement,
  AgentRosterCardElement,
  LiveRunPanelElement,
  TextBlockElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const ROSTER: AgentRosterCardElement = {
  kind: 'agent-roster-card',
  id: 'cascade-agent-roster',
  title: 'Cascade 3PL — Agents — 8 active across 4 facilities',
  rows: [
    {
      id: 'agent-trailmix-bulkship',
      name: 'Trailmix bulk-ship — Indy 10am',
      cadence: 'Daily 10:00 ET',
      lastRun: 'Today 10:02 · ✓ 3,200 labels',
      status: 'active',
      emphasize: true,
    },
    {
      id: 'agent-briar-bulkship',
      name: 'Briar Coffee bulk-ship — Atlanta 8am',
      cadence: 'Daily 8:00 ET',
      lastRun: 'Today 8:01 · ✓ 2,150 labels',
      status: 'active',
    },
    {
      id: 'agent-northwind-bulkship',
      name: 'Northwind bulk-ship — Indy 1pm',
      cadence: 'Daily 13:00 ET',
      lastRun: 'Scheduled · in 2h 47m',
      status: 'active',
    },
    {
      id: 'agent-oakhill-bulkship',
      name: 'OakHill bulk-ship — Reno 9am PT',
      cadence: 'Daily 9:00 PT',
      lastRun: 'Today 9:01 PT · ✓ 1,180 labels',
      status: 'active',
    },
    {
      id: 'agent-altitude-bulkship',
      name: 'Altitude bulk-ship — Charlotte 11am',
      cadence: 'Daily 11:00 ET',
      lastRun: 'Today 11:02 · ✓ 1,420 labels',
      status: 'active',
    },
    {
      id: 'agent-post-shipment',
      name: 'Post-shipment monitor',
      cadence: 'Continuous',
      lastRun: '14 min ago · 285 exceptions this week',
      status: 'active',
    },
    {
      id: 'agent-claims',
      name: 'Carrier claims',
      cadence: 'Daily 7:00 ET',
      lastRun: 'Today 7:00 · ✓ 11 filed',
      status: 'active',
    },
    {
      id: 'agent-margin-watch',
      name: 'Margin trajectory watch',
      cadence: 'Daily 4:00 ET',
      lastRun: 'Today 4:00 · ✓ Northwind flagged',
      status: 'active',
    },
  ],
  footer: '0 failed runs across all 8 agents in the last 30 days.',
}

const TRAILMIX_DETAIL: AgentDetailCardElement = {
  kind: 'agent-detail-card',
  id: 'agent-trailmix-detail',
  tag: 'Bulk-ship · Daily 10:00 ET · Indy',
  name: 'Trailmix bulk-ship — Indy 10am',
  description:
    'Every weekday at 10am, batch and print labels for all Trailmix Provisions orders ready to ship from the Indianapolis bulk stations. Pause if order count is below 1,500 or above 4,500.',
  recentRuns: [
    { when: 'Today 10:02', summary: '3,200 labels · 12 min', tone: 'success' },
    { when: 'Mon 10:01', summary: '3,084 labels · 12 min', tone: 'success' },
    { when: 'Fri 10:02', summary: '3,485 labels · 13 min', tone: 'success' },
    { when: 'Thu 10:01', summary: '3,210 labels · 12 min', tone: 'success' },
    { when: 'Wed 10:02', summary: '3,316 labels · 12 min', tone: 'success' },
    { when: 'Tue 10:01', summary: '2,955 labels · 11 min', tone: 'success' },
  ],
  trackRecord: '30 runs · 100% success rate · ~12 min average',
  config: [
    { label: 'Brand', value: 'Trailmix Provisions (TMX)' },
    { label: 'Facility', value: 'Indianapolis HQ' },
    { label: 'Pack stations', value: 'Bulk-1 through Bulk-6' },
    { label: 'Printers', value: 'Zebra-Bulk-East-1, Zebra-Bulk-East-2' },
    { label: 'Delivery', value: 'Slack #ops-leads · per-run confirmation' },
  ],
  failSafe: 'Pause if order count is below 1,500 or above 4,500.',
}

const RUN_TITLE = 'Trailmix bulk-ship — live'

const RUN_INITIAL: LiveRunPanelElement = {
  kind: 'live-run-panel',
  id: 'agent-trailmix-run',
  title: RUN_TITLE,
  state: 'running',
  steps: [{ tone: 'pending', text: 'Starting…' }],
}

const RUN_AFTER_PULL: LiveRunPanelElement = {
  kind: 'live-run-panel',
  id: 'agent-trailmix-run',
  title: RUN_TITLE,
  state: 'paused',
  steps: [
    { tone: 'info', text: 'Trailmix bulk-ship — starting' },
    {
      tone: 'success',
      text: 'Pulled open orders for Trailmix Provisions',
      detail: '1,420 orders matched',
    },
    {
      tone: 'warning',
      text: 'Below configured floor (1,500)',
      detail: 'Pausing for approval',
    },
  ],
  prompt: {
    kind: 'pause',
    headline: '1,420 orders is below the configured floor of 1,500.',
    body: 'Usually an upstream issue (slow morning, sync delay) — or a real slow day. Type continue, skip, or investigate to decide.',
  },
}

const RUN_AFTER_INVESTIGATE: LiveRunPanelElement = {
  kind: 'live-run-panel',
  id: 'agent-trailmix-run',
  title: RUN_TITLE,
  state: 'paused',
  steps: [
    { tone: 'info', text: 'Trailmix bulk-ship — starting' },
    {
      tone: 'success',
      text: 'Pulled open orders for Trailmix Provisions',
      detail: '1,420 orders matched',
    },
    {
      tone: 'warning',
      text: 'Below configured floor (1,500)',
      detail: 'Pausing for approval',
    },
    { tone: 'info', text: 'Checking Trailmix order ingestion (last 60 min)' },
    {
      tone: 'success',
      text: '180 new orders in the last 10 min',
      detail: 'Pace consistent with normal morning',
    },
  ],
  prompt: {
    kind: 'recommendation',
    headline:
      'Recommendation: wait 15 min and retry — likely to clear the 1,500 floor.',
    body: 'Or proceed with 1,420 now if Cascade wants to ship.',
  },
}

const BEAT1_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'beat1-closer',
  text: 'Trailmix 10:02am · 3,200 labels (Indy). Briar 8:01am · 2,150 labels (Atlanta). **Open the Trailmix agent to see what just happened.**',
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'beat2-closer',
  text: '30 runs, ~12 min each, fail-safe 1,500–4,500. **Want to see one run live?**',
}

const BEAT3_PAUSE_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'beat3-pause-closer',
  text: "1,420 below the 1,500 floor. **Type 'investigate' to check ingestion, or 'continue' to ship anyway.**",
}

const BEAT3_RECOMMENDATION_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'beat3-recommendation-closer',
  text: '180 new orders in the last 10 min — pace looks normal. **Want to add an agent for a different brand?**',
}

/**
 * Cascade 3PL — Demo 2: Bulk Shipping Label Printing Agent.
 *
 * Beat 1: Agents tab roster. Eight agents ran this morning across 4
 * facilities without anyone touching them.
 *
 * Beat 2: Drill into the Trailmix agent. Plain-language description, 30 runs
 * of clean history, the fail-safe range that earns trust.
 *
 * Beat 3: "Run now" — 1,420 orders today, below the 1,500 floor. Pause.
 * User types "investigate" → ingestion check shows 180 new orders in the
 * last 10 min. Recommendation: wait 15 min.
 *
 * Beat 4: Chat-first agent creation. "What should this agent do?" The
 * demoer pastes a spec; the new Northwind 1pm agent gets created.
 */
export const bulkLabelAgent: DemoChatScript = {
  id: 'bulk-label-agent',
  name: 'Bulk Shipping Label Printing Agent',
  description:
    'Demo 2. The "morning is already done" moment — Neo runs the 2-hour label-batching routine across 4 facilities, pauses when volume looks off, asks for judgment instead of acting blindly.',
  triggers: [
    'agents',
    'agent',
    'show me the agents',
    'show agents',
    'show me agents',
    'walk me through the agents',
    'walk me through agents',
    'open agents',
    'look at agents',
    'whats running',
    "what's running",
    'whats this morning',
    "what's this morning",
    'morning runs',
    'morning routine',
    'morning',
    'bulk ship',
    'bulk-ship',
    'bulkship',
    'bulk label',
    'bulk labels',
    'label printing',
    'print labels',
    'labels',
    'batching',
    'two hours of label printing',
    'two hour morning',
    'trailmix',
    'trailmix bulk',
    'briar',
    'briar coffee',
    'run now',
    'run the agent',
    'show me a run',
    'live run',
  ],
  followUps: [
    {
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'go',
        'go ahead',
        'do it',
        'please',
        'continue',
        'next',
        "what's next",
        'whats next',
        'show me',
        'show me more',
        'more',
        'keep going',
        'claims',
        'carrier claims',
        'money',
        'recovery',
      ],
      nextScriptId: 'carrier-claims-agent',
    },
    {
      triggers: [
        'post-shipment',
        'post shipment',
        'delays',
        'customer service',
        'exceptions',
      ],
      nextScriptId: 'post-shipment-support',
    },
  ],
  events: [
    // ─────────── Beat 1: Agents tab roster ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Loading agent roster from your account...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Eight active agents across 4 facilities. Four morning bulk-ship runs already completed today — pulling their run summaries.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'list_neo_jobs' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: '8 active, 0 failed runs in 30 days.',
    },
    { type: 'demo-element-add', delayMs: 600, element: ROSTER },
    { type: 'demo-element-add', delayMs: 700, element: BEAT1_CLOSER },
    { type: 'done', delayMs: 400, title: 'Agents — morning roster' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: Trailmix agent detail ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Loading Trailmix bulk-ship agent...',
    },
    { type: 'tool-call', delayMs: 300, name: 'get_neo_job' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: 'Trailmix bulk-ship · 30 runs · 100% success.',
    },
    { type: 'demo-element-add', delayMs: 600, element: TRAILMIX_DETAIL },
    { type: 'demo-element-add', delayMs: 700, element: BEAT2_CLOSER },
    { type: 'done', delayMs: 400, title: 'Trailmix bulk-ship — detail' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3a: live run starts, hits the floor ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Starting Trailmix bulk-ship — live...',
    },
    { type: 'tool-call', delayMs: 200, name: 'run_neo_job_now' },
    { type: 'demo-element-add', delayMs: 400, element: RUN_INITIAL },
    {
      type: 'demo-element-update',
      delayMs: 1400,
      element: RUN_AFTER_PULL,
    },
    {
      type: 'text-delta',
      delayMs: 600,
      delta: '1,420 orders matched. Below the 1,500 floor.',
    },
    { type: 'demo-element-add', delayMs: 700, element: BEAT3_PAUSE_CLOSER },
    { type: 'done', delayMs: 400, title: 'Trailmix bulk-ship — paused' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3b: investigation result ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Checking Trailmix order ingestion (last 60 min)...',
    },
    { type: 'tool-call', delayMs: 200, name: 'check_order_ingestion' },
    {
      type: 'demo-element-update',
      delayMs: 600,
      element: RUN_AFTER_INVESTIGATE,
    },
    {
      type: 'text-delta',
      delayMs: 600,
      delta: '180 new orders in the last 10 min.',
    },
    {
      type: 'demo-element-add',
      delayMs: 700,
      element: BEAT3_RECOMMENDATION_CLOSER,
    },
    {
      type: 'done',
      delayMs: 400,
      title: 'Trailmix bulk-ship — recommendation',
    },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 4: chat-first agent creation ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Starting agent creation...',
    },
    {
      type: 'text-delta',
      delayMs: 400,
      delta: 'Tell me what the agent should do, in plain English.',
    },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: {
        kind: 'text-block',
        id: 'beat4-example',
        text: '_Example: "Bulk-ship Northwind Apparel orders every weekday at 1pm out of Indianapolis. Same Bulk-1–6 stations and Zebra-Bulk-East printers. Pause if it\'s under 1,500 or over 4,500."_',
      },
    },
    { type: 'done', delayMs: 300, title: 'New agent — chat-first' },
    { type: 'await-user-prompt', delayMs: 0 },

    { type: 'status', delayMs: 200, message: 'Drafting agent config...' },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Brand: Northwind Apparel. Cadence: weekdays at 13:00 ET. Facility: Indianapolis. Pack stations: Bulk-1 through Bulk-6. Printers: Zebra-Bulk-East-1 / -2. Fail-safe floor 1,500, ceiling 4,500. Confirmation channel: Slack #ops-leads.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'create_neo_job' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'Got it. Northwind bulk-ship — Mon–Fri 1pm, floor 1,500, ceiling 4,500. First run on the next weekday at 1pm ET.',
    },
    {
      type: 'approval',
      delayMs: 600,
      proposal: {
        proposalId: 'demo-cascade-bulk-new-agent',
        toolName: 'create_neo_job',
        actionType: 'agent_creation',
        description:
          'Create agent "Northwind bulk-ship — Indy 1pm". Brand: Northwind Apparel. Cadence: Mon–Fri 13:00 ET. Facility: Indianapolis HQ. Pack stations: Bulk-1 through Bulk-6. Printers: Zebra-Bulk-East-1, Zebra-Bulk-East-2. Fail-safe: 1,500–4,500. Output: Slack #ops-leads.',
      },
    },
    { type: 'done', delayMs: 400, title: 'Northwind 1pm agent — confirm' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-bulk-new-agent',
    },

    // ─────────── Wrap: cue toward the carrier claims demo ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Northwind agent registered.',
    },
    {
      type: 'text-delta',
      delayMs: 400,
      delta:
        'Northwind 1pm scheduled. Three of the eight agents handle money — claims, refunds, recovery.',
    },
    {
      type: 'demo-element-add',
      delayMs: 500,
      element: {
        kind: 'text-block',
        id: 'bulk-wrap',
        text: '**Want to see what carrier claims looks like next?**',
      },
    },
    { type: 'done', delayMs: 400, title: 'Bulk label demo — complete' },
  ],
}
